// Cortex pipeline canvas — the STRATEGY-as-node projection of the typed
// strategy DAG, drawn as a typed node-graph editor.
//
// The cortex model is a typed node graph already: a `strategy_kind` is a node
// type (domain_storage_type = its input socket type, codomain_storage_type = its
// output socket type, flow_endpoint = its implementation, params_schema = its
// widgets, query_strategy_kinds = the node catalog). This canvas draws it as:
//   - each STRATEGY is a NODE: title = kind display name, one typed INPUT socket
//     (its domain storage type) and one typed OUTPUT socket (its codomain type),
//     params shown in the node body.
//   - each source STORAGE (no incoming strategy) is a "Load <type>" node:
//     no input, one typed output.
//   - each target STORAGE (no outgoing strategy) is a "Save <type>" node:
//     one typed input, no output.
//   - the intermediate storages become the LINKS between strategy nodes (the
//     data flowing on the wire), colored by storage type. A link is valid iff
//     the upstream codomain type == the downstream domain type — the SAME rule
//     create_strategy enforces server-side (tools.js).
//
// P1 is READ-ONLY: it projects the existing storages+strategies into this view
// and shows the kind palette. Editing (drag-create kind nodes, type-checked
// linking, widget edits → create/update/delete_strategy) is P2.
import React, { useCallback, useMemo, useEffect, useState, useRef } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import "../../flow-studio/mount/theme-cortex.css";
import { FileText, Boxes, Database, Server, Upload, Download, Cpu, Search, ChevronDown, ChevronRight, ChevronUp, Plus, X, Cloud, Mail, MessageSquare, Globe, Workflow as WorkflowIcon, Pencil } from "lucide-react";
import { updateNode } from "../../services/pipeline";

// Storage type → color + icon. Open set: unknown types get a stable fallback so
// new storage types (media, llm, ...) still render. Socket/link color is derived
// from the storage type (one color per type, like a node editor's link colors).
const STORAGE_TYPE = {
  file:              { label: "file",              color: "#21A366", icon: FileText },
  parquet_raw:       { label: "parquet_raw",       color: "#8B5CF6", icon: Boxes },
  parquet_processed: { label: "parquet_processed", color: "#6366F1", icon: Boxes },
  postgres:          { label: "postgres",          color: "#377AD6", icon: Database },
  mysql:             { label: "mysql",             color: "#00758F", icon: Database },
  mongo:             { label: "mongo",             color: "#10B981", icon: Database },
  sqlserver:         { label: "sqlserver",         color: "#A91D22", icon: Database },
  oracle:            { label: "oracle",            color: "#C74634", icon: Database },
  snowflake:         { label: "snowflake",         color: "#29B5E8", icon: Cloud },
  bigquery:          { label: "bigquery",          color: "#4285F4", icon: Cloud },
  redshift:          { label: "redshift",          color: "#C925D1", icon: Cloud },
  s3:                { label: "s3",                color: "#E25A1C", icon: Cloud },
  gmail:             { label: "gmail",             color: "#EA4335", icon: Mail },
  slack:             { label: "slack",             color: "#611f69", icon: MessageSquare },
  api:               { label: "External API",      color: "#0EA5E9", icon: Globe },
  scraper:           { label: "Scraper",           color: "#0D9488", icon: Search },
};
export const storageType = (t) => STORAGE_TYPE[t] || { label: t || "storage", color: "#94a3b8", icon: Server };
const typeColor = (t) => storageType(t).color;

// Source vs target classification: a storage with no incoming strategy is a
// "Load" (source); one with no outgoing strategy is a "Save" (target). Storages
// that are both produced and consumed are intermediates → they become links.
const SOURCE_TYPES = new Set(["file"]);                 // pipeline entry points
const TARGET_TYPES = new Set(["postgres", "mysql", "mongo"]); // sinks

const CARD_W = 200;
const COL_GAP = 130;
const ROW_GAP = 36;

// ── Custom nodes ────────────────────────────────────────────────────────────
// The product model: EVERYTHING IS A SOURCE. You connect Source -> Source
// ("stream A into B"); between them sits a STRATEGY node — the effect applied
// while streaming records INTO the consuming (downstream) source.

// Editable node title: the text is the node's `label` (PUT /api/nodes/:id),
// renamed in place via the hover pencil. Deliberately NOT double-click —
// dblclick opens the node's detail — and the input swallows every event so
// ReactFlow doesn't drag the node or treat Backspace as "delete node".
// Only node-model rows (data.node.id) are renamable; legacy/pending nodes
// render the plain title.
function EditableTitle({ data, value, bold }) {
  const rawId = data?.node?.id;
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(null); // optimistic until the DAG poll confirms
  const [draft, setDraft] = useState("");
  const shown = local ?? value;
  const commit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (!next || next === shown) return;
    try {
      await updateNode(rawId, { label: next });
      setLocal(next);
    } catch (e) {
      console.error("node rename failed:", e?.message || e);
    }
  };
  if (editing) {
    return (
      <input
        autoFocus
        data-testid="node-label-input"
        defaultValue={shown}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        className="nodrag"
        style={{
          background: "var(--flow-node-bg)", color: "var(--flow-text)",
          border: "1px solid #8B5CF6", borderRadius: 4, padding: "1px 4px",
          fontSize: 12, fontWeight: bold ? 600 : 500, width: "100%", minWidth: 110, textAlign: "center",
        }}
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-1 max-w-full">
      <span style={{ fontWeight: bold ? 600 : 500 }} className="truncate">{shown}</span>
      {rawId != null && (
        <button
          type="button"
          data-testid="node-label-edit"
          title="Rename node"
          className="opacity-0 group-hover:opacity-70 hover:!opacity-100 shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); setDraft(shown); setEditing(true); }}
        >
          <Pencil size={10} />
        </button>
      )}
    </span>
  );
}

// A STRATEGY node: the intermediate effect on a stream (processing-rules,
// deduplication, enrichment, plain stream-into). Typed input (left, upstream
// type) + typed output (right, target type). title = strategy display name.
function StrategyNode({ data }) {
  const { kind, domainType, codomainType, paramCount, groupCount, groupKey, groupExpanded } = data;
  const grouped = (groupCount || 0) > 1;
  return (
    <div
      className="group"
      style={{
        background: "color-mix(in srgb, #8B5CF6 14%, var(--flow-node-bg))",
        border: "2px dashed #8B5CF6",
        borderRadius: 8, padding: "8px 12px", fontSize: 12, minWidth: CARD_W,
        color: "var(--flow-text)", cursor: "pointer",
        // a subtle "stack" shadow signals the card stands for N instances
        boxShadow: grouped ? "3px 3px 0 0 color-mix(in srgb, #8B5CF6 35%, transparent), 6px 6px 0 0 color-mix(in srgb, #8B5CF6 18%, transparent)" : undefined,
      }}
    >
      <Handle type="target" position={Position.Left} id="in"
        style={{ background: typeColor(domainType), width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
      <div className="flex items-center justify-center gap-1.5">
        <Cpu size={13} />
        <EditableTitle data={data} value={data.displayName || kind} bold />
        {grouped && (
          <span data-testid="strategy-group-badge"
            style={{ fontSize: 10, fontWeight: 700, color: "#8B5CF6", background: "color-mix(in srgb, #8B5CF6 18%, transparent)", borderRadius: 8, padding: "0 6px" }}>
            ×{groupCount}
          </span>
        )}
      </div>
      <div style={{ fontSize: 9, color: "var(--flow-text-muted)", marginTop: 3, textAlign: "center" }}>
        {grouped ? `strategy · ${groupCount} parallel instances` : "strategy"}
      </div>
      {!grouped && paramCount > 0 && (
        <div style={{ fontSize: 8, color: "var(--flow-text-muted)", marginTop: 2, textAlign: "center" }}>
          {paramCount} param{paramCount === 1 ? "" : "s"}
        </div>
      )}
      {(grouped || groupExpanded) && (
        <button
          data-testid={grouped ? "strategy-group-expand" : "strategy-group-collapse"}
          title={grouped ? "Show the individual instances" : "Collapse back into one card"}
          className="nodrag"
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); data.onToggleGroup?.(groupKey); }}
          style={{ position: "absolute", top: 3, right: 3, border: 0, background: "transparent", cursor: "pointer", color: "var(--flow-text-muted)", padding: 1, lineHeight: 0 }}
        >
          {grouped ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
      )}
      <Handle type="source" position={Position.Right} id="out"
        style={{ background: typeColor(codomainType), width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
    </div>
  );
}

// A SOURCE node: a data source of some TYPE (file / postgres / mongo / …). Any
// source can both PRODUCE records (stream out) and CONSUME records (stream in),
// so it carries BOTH handles. Which side a given source ends up on is decided by
// what the user wires — and whether a strategy exists for streaming into it.
function SourceNode({ data }) {
  const info = storageType(data.storageType);
  const Icon = info.icon;
  return (
    <div className="group" style={{
      background: `color-mix(in srgb, ${info.color} 20%, var(--flow-node-bg))`,
      border: `2px solid ${info.color}`, borderRadius: 8, padding: "8px 12px",
      fontSize: 12, minWidth: 150, textAlign: "center", color: "var(--flow-text)", cursor: "pointer",
    }}>
      <Handle type="target" position={Position.Left} id="in"
        style={{ background: info.color, width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
      <div className="flex items-center justify-center gap-1.5">
        <Icon size={13} />
        <EditableTitle data={data} value={data.name} />
      </div>
      <div style={{ fontSize: 9, color: "var(--flow-text-muted)", marginTop: 2 }}>Source · {info.label}</div>
      <Handle type="source" position={Position.Right} id="out"
        style={{ background: info.color, width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
    </div>
  );
}

// load/save both render as Source; transform renders as Strategy. (The role keys
// stay in the data layer / executor; the product shows only Source + Strategy.)
const nodeTypes = { strategy: StrategyNode, load: SourceNode, save: SourceNode };

// ── Projection: storages + strategies → typed node graph ──────────────────────
//
// Nodes:
//   - one `load` node per source storage (no incoming strategy)
//   - one `save` node per target storage (no outgoing strategy)
//   - one `strategy` node per strategy row
// Links:
//   - for each strategy M: connect whatever PRODUCES M's domain storage to M.in,
//     and connect M.out to whatever CONSUMES M's codomain storage.
//   A storage is produced by the strategy whose codomain == it, or by a load
//   node if it's a source storage. It is consumed by the strategy whose domain
//   == it, or by a save node if it's a target storage.
export function buildPipelineGraph(storages, strategies, kindsByName, savedPositions) {
  const byId = new Map((storages || []).map(s => [s.storage_id, s]));
  const ms = strategies || [];

  const hasIncoming = new Set(ms.map(m => m.codomain_storage_id));
  const hasOutgoing = new Set(ms.map(m => m.domain_storage_id));

  // Producer of a storage id: the strategy node that outputs it, or a load node.
  const producerNodeId = (sid) => {
    const m = ms.find(mm => mm.codomain_storage_id === sid);
    if (m) return `m:${m.id}`;
    return `load:${sid}`; // source storage
  };
  // Consumers of a storage id: strategy nodes that take it as domain, or save node.
  const consumerNodeIds = (sid) => {
    const cs = ms.filter(mm => mm.domain_storage_id === sid).map(mm => `m:${mm.id}`);
    if (cs.length) return cs;
    return [`save:${sid}`]; // target storage
  };

  const nodes = [];
  const edges = [];
  const seenLoad = new Set();
  const seenSave = new Set();

  // Strategy nodes.
  for (const m of ms) {
    const k = kindsByName?.get(m.kind);
    const domainType = String(m.domain_storage_id).split(":")[0];
    const codomainType = String(m.codomain_storage_id).split(":")[0];
    let paramCount = 0;
    try { paramCount = m.params ? Object.keys(typeof m.params === "string" ? JSON.parse(m.params) : m.params).length : 0; } catch {}
    nodes.push({
      id: `m:${m.id}`, type: "strategy", position: { x: 0, y: 0 },
      data: {
        kind: m.kind, displayName: k?.display_name, domainType, codomainType, paramCount,
        strategy: m, kindDef: k,
      },
    });
  }

  // Load nodes for source storages; Save nodes for target storages.
  for (const s of storages || []) {
    const sid = s.storage_id;
    if (!hasIncoming.has(sid)) {
      // produced by nothing → it's a source (Load)
      if (!seenLoad.has(sid)) {
        seenLoad.add(sid);
        nodes.push({ id: `load:${sid}`, type: "load", position: { x: 0, y: 0 },
          data: { name: s.name, storageType: s.storage_type, storage: s } });
      }
    }
    if (!hasOutgoing.has(sid)) {
      // consumed by nothing → it's a target (Save)
      if (!seenSave.has(sid)) {
        seenSave.add(sid);
        nodes.push({ id: `save:${sid}`, type: "save", position: { x: 0, y: 0 },
          data: { name: s.name, storageType: s.storage_type, storage: s } });
      }
    }
  }

  // Links: walk each strategy's domain (incoming wire) — the producer → this
  // strategy. The codomain wires are covered when we process the consuming
  // strategy's domain, EXCEPT the final hop into a Save node, handled below.
  for (const m of ms) {
    const inSid = m.domain_storage_id;
    const prod = producerNodeId(inSid);
    edges.push({
      id: `e:${prod}->m:${m.id}`, source: prod, target: `m:${m.id}`,
      sourceHandle: "out", targetHandle: "in",
      style: { stroke: typeColor(String(inSid).split(":")[0]), strokeWidth: 2 },
      label: byId.get(inSid)?.name || inSid,
      labelStyle: { fill: "var(--flow-text-muted)", fontSize: 8 },
      labelBgStyle: { fill: "var(--flow-edge-textbg, #1a2238)", fillOpacity: 0.7 },
      labelBgPadding: [3, 1], labelBgBorderRadius: 3,
      markerEnd: { type: "arrowclosed", color: typeColor(String(inSid).split(":")[0]) },
    });
  }
  // Final hop: strategy → Save (codomain consumed by nothing).
  for (const m of ms) {
    const outSid = m.codomain_storage_id;
    if (!hasOutgoing.has(outSid)) {
      for (const cons of consumerNodeIds(outSid)) {
        edges.push({
          id: `e:m:${m.id}->${cons}`, source: `m:${m.id}`, target: cons,
          sourceHandle: "out", targetHandle: "in",
          style: { stroke: typeColor(String(outSid).split(":")[0]), strokeWidth: 2 },
          label: byId.get(outSid)?.name || outSid,
          labelStyle: { fill: "var(--flow-text-muted)", fontSize: 8 },
          labelBgStyle: { fill: "var(--flow-edge-textbg, #1a2238)", fillOpacity: 0.7 },
          labelBgPadding: [3, 1], labelBgBorderRadius: 3,
          markerEnd: { type: "arrowclosed", color: typeColor(String(outSid).split(":")[0]) },
        });
      }
    }
  }

  // ── Deterministic left→right layered layout (topological columns) ──────────
  // Assign each node a column = longest path from any load node; stack within.
  const incoming = new Map(nodes.map(n => [n.id, []]));
  for (const e of edges) incoming.get(e.target)?.push(e.source);
  const colOf = new Map();
  const computeCol = (id, guard = new Set()) => {
    if (colOf.has(id)) return colOf.get(id);
    if (guard.has(id)) return 0;
    guard.add(id);
    const preds = incoming.get(id) || [];
    const c = preds.length ? 1 + Math.max(...preds.map(p => computeCol(p, guard))) : 0;
    colOf.set(id, c);
    return c;
  };
  for (const n of nodes) computeCol(n.id);
  const cols = new Map();
  for (const n of nodes) {
    const c = colOf.get(n.id) || 0;
    if (!cols.has(c)) cols.set(c, []);
    cols.get(c).push(n);
  }
  const maxRows = Math.max(1, ...[...cols.values()].map(l => l.length));
  const span = maxRows * (60 + ROW_GAP);
  for (const [c, list] of cols) {
    list.sort((a, b) => a.id.localeCompare(b.id));
    const h = list.length * (60 + ROW_GAP);
    const yOff = (span - h) / 2;
    list.forEach((n, i) => { n.position = { x: c * (CARD_W + COL_GAP), y: yOff + i * (60 + ROW_GAP) }; });
  }

  // Honor any saved (dragged) position over the computed layout slot.
  if (savedPositions) {
    for (const n of nodes) {
      const p = savedPositions[n.id];
      if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) n.position = { x: p.x, y: p.y };
    }
  }

  return { nodes, edges };
}

// Build the reactflow graph DIRECTLY from the node/link authoring model — the
// real pipeline graph (load/transform/save nodes + links). This is the canonical
// renderer now; buildPipelineGraph (the storages/strategies projection) is the
// legacy fallback for projects with no node rows yet. Intermediate parquet
// storages are NOT nodes here — they are the links between transforms.
export function buildGraphFromNodes(ng, savedPositions, expandedGroups) {
  const rawNodes = (ng && ng.nodes) || [];
  const links = (ng && ng.links) || [];
  const nodes = rawNodes.map((n) => {
    if (n.role === "load") {
      return { id: `n:${n.id}`, type: "load", position: { x: 0, y: 0 },
        data: { name: n.label, storageType: n.storage_type_out || "file", node: n,
                storage: n.data_source_id != null ? { storage_id: `${n.storage_type_out}:${n.data_source_id}`, storage_type: n.storage_type_out, name: n.label, entity_id: n.data_source_id, origin: 'data_source' } : null } };
    }
    if (n.role === "save") {
      return { id: `n:${n.id}`, type: "save", position: { x: 0, y: 0 },
        data: { name: n.label, storageType: n.storage_type_in || "postgres", node: n,
                storage: n.data_target_id != null ? { storage_id: `${n.storage_type_in}:${n.data_target_id}`, storage_type: n.storage_type_in, name: n.label, entity_id: n.data_target_id, origin: 'data_target' } : null } };
    }
    // transform
    let paramCount = 0;
    try { const p = typeof n.params === "string" ? JSON.parse(n.params) : (n.params || {}); paramCount = Object.keys(p).filter(k => !k.startsWith("__")).length; } catch {}
    return { id: `n:${n.id}`, type: "strategy", position: { x: 0, y: 0 },
      data: { kind: n.kind, displayName: n.label, domainType: n.storage_type_in, codomainType: n.storage_type_out, paramCount, node: n,
              // expose a strategy-shaped object so the inspector + delete handler work
              strategy: { id: n.id, kind: n.kind, __node: true, inType: n.storage_type_in, outType: n.storage_type_out,
                          params: (() => { try { const p = typeof n.params === "string" ? JSON.parse(n.params) : (n.params||{}); const o={}; for (const k of Object.keys(p)) if(!k.startsWith("__")) o[k]=p[k]; return o; } catch { return {}; } })() } } };
  });
  const idset = new Set(nodes.map(n => n.id));
  // Role by raw node id, to enforce port direction: a LOAD node is a pure source
  // (output only — never the TARGET of an edge); a SAVE node is a pure sink
  // (input only — never the SOURCE of an edge). Drop any link that violates this
  // so a connector can't render with the wrong-side handle (file/postgres/mongo
  // load nodes all behave identically: no input).
  const roleById = new Map(rawNodes.map(n => [n.id, n.role]));
  // Port-direction rule. A strategy (transform) streams source→strategy→source,
  // so a strategy's INBOUND comes from a source and its OUTBOUND goes INTO a
  // source — i.e. a load-role node IS a valid edge TARGET when the edge comes
  // FROM a transform (the codomain it streams into). Only a SOURCE→SOURCE direct
  // edge (no strategy between) is invalid. So: drop a link only if it would put a
  // load node on the receiving end of ANOTHER source (from-role load/save), or a
  // save node as a source. transform→load and load→transform are both valid.
  const edges = (links || [])
    .filter(l => idset.has(`n:${l.from_node_id}`) && idset.has(`n:${l.to_node_id}`))
    .filter(l => {
      const fr = roleById.get(l.from_node_id), tr = roleById.get(l.to_node_id);
      // A save node MAY feed a transform: post-load steps (e.g. cross-field DQ
      // flags) read the loaded target and write back into it — the graph is
      // save → transform → save. Only save→save / save→load stay invalid.
      if (fr === "save" && tr !== "transform") return false;
      if (tr === "load" && fr !== "transform") return false; // only a strategy may stream INTO a source
      return true;
    })
    .map(l => ({
      id: `l:${l.id}`, source: `n:${l.from_node_id}`, target: `n:${l.to_node_id}`,
      sourceHandle: "out", targetHandle: "in", type: "default",
      style: { stroke: "#8b5cf6", strokeWidth: 1.8 },
      markerEnd: { type: "arrowclosed", color: "#8b5cf6" },
      data: { link: l },
    }));

  // Write-back steps (post-load transform materialising into the target it
  // read) are drawn ACYCLICALLY as node entities: a SECOND save node of the
  // same data_target, linked transform → save (POST /api/nodes/place-source
  // with allow_duplicate + the derived-surface link allowance). No synthetic
  // edges — what the canvas shows is exactly the node/link rows.

  // ── VISUAL COLLAPSE of parallel identical transforms ──────────────────────
  // N transform nodes of the same kind, with identical user-facing params,
  // streaming into the same target(s), carry zero extra information as N
  // cards (all semantics is in the edges) — draw ONE card with a ×N badge.
  // Purely presentational: the strategy rows / node rows are untouched, and a
  // group is re-expandable per-canvas via the card's chevron (expandedGroups).
  {
    const outsByNode = new Map();
    for (const e of edges) {
      if (!outsByNode.has(e.source)) outsByNode.set(e.source, []);
      outsByNode.get(e.source).push(e.target);
    }
    const groups = new Map(); // sig -> [visual nodes]
    for (const n of nodes) {
      if (n.type !== "strategy") continue;
      const sig = `${n.data.kind}|${JSON.stringify(n.data.strategy?.params || {})}|${(outsByNode.get(n.id) || []).slice().sort().join(",")}`;
      if (!groups.has(sig)) groups.set(sig, []);
      groups.get(sig).push(n);
    }
    for (const [sig, members] of groups) {
      if (members.length < 2) continue;
      if (expandedGroups && expandedGroups.has(sig)) {
        // stay expanded — but let each member card offer "collapse" back
        for (const m of members) { m.data.groupKey = sig; m.data.groupExpanded = true; }
        continue;
      }
      const memberIds = new Set(members.map((m) => m.id));
      const first = members[0];
      const gid = `grp:${first.id}`;
      const gnode = {
        id: gid, type: "strategy", position: { x: 0, y: 0 },
        data: {
          ...first.data,
          displayName: first.data.displayName || first.data.kind,
          groupKey: sig, groupCount: members.length,
          memberNodeIds: members.map((m) => m.data.node?.id).filter((x) => x != null),
          // group semantics for the click/delete handlers: the strategy object
          // is the FIRST member's, marked as a group (config apps must not
          // scope to one member's source; delete must expand first).
          strategy: { ...(first.data.strategy || {}), __groupCount: members.length },
        },
      };
      const kept = nodes.filter((n) => !memberIds.has(n.id));
      kept.push(gnode);
      nodes.length = 0; nodes.push(...kept);
      const seen = new Set();
      const rewired = [];
      for (const e of edges) {
        const src = memberIds.has(e.source) ? gid : e.source;
        const tgt = memberIds.has(e.target) ? gid : e.target;
        const key = `${src}->${tgt}`;
        if (src === tgt) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        rewired.push({ ...e, id: memberIds.has(e.source) || memberIds.has(e.target) ? `g:${e.id}` : e.id, source: src, target: tgt });
      }
      edges.length = 0; edges.push(...rewired);
    }
  }

  // Layered left→right layout (same algorithm as buildPipelineGraph).
  const incoming = new Map(nodes.map(n => [n.id, []]));
  for (const e of edges) incoming.get(e.target)?.push(e.source);
  const colOf = new Map();
  const computeCol = (id, guard = new Set()) => {
    if (colOf.has(id)) return colOf.get(id);
    if (guard.has(id)) return 0;
    guard.add(id);
    const preds = incoming.get(id) || [];
    const c = preds.length ? 1 + Math.max(...preds.map(p => computeCol(p, guard))) : 0;
    colOf.set(id, c); return c;
  };
  for (const n of nodes) computeCol(n.id);
  const cols = new Map();
  for (const n of nodes) { const c = colOf.get(n.id) || 0; if (!cols.has(c)) cols.set(c, []); cols.get(c).push(n); }
  const maxRows = Math.max(1, ...[...cols.values()].map(l => l.length));
  const span = maxRows * (60 + ROW_GAP);
  for (const [c, list] of cols) {
    list.sort((a, b) => a.id.localeCompare(b.id));
    const h = list.length * (60 + ROW_GAP);
    const yOff = (span - h) / 2;
    list.forEach((n, i) => { n.position = { x: c * (CARD_W + COL_GAP), y: yOff + i * (60 + ROW_GAP) }; });
  }
  // A node's OWN stored position (DB `position` column — set when dropped at a
  // cursor or when a strategy is placed at the midpoint of its two sources) wins
  // over the auto-layout. localStorage savedPositions (per-browser drag state)
  // wins over both, so a user's manual drag is always respected last.
  for (const n of nodes) {
    const p = n.data?.node?.position;
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) n.position = { x: p.x, y: p.y };
  }
  if (savedPositions) for (const n of nodes) { const p = savedPositions[n.id]; if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) n.position = { x: p.x, y: p.y }; }
  return { nodes, edges };
}

// ── Node library: catalog of strategy KINDS organized by category ────────────
// The kind registry (query_strategy_kinds) is the node catalog — the equivalent
// of ComfyUI's /object_info. Each kind's `category` is a slash-delimited path
// ("load/relational"); we parse those paths into a nested folder TREE and render
// a collapsible left-side explorer, like ComfyUI's Node Library sidebar. A leaf
// is a strategy kind — click or drag it onto the canvas to drop a node.

// buildKindTree: flat kinds + a path-extractor → nested tree.
// Mirrors ComfyUI's buildTree(items, key): split each kind's category path into
// folder segments and nest, attaching the kind to the leaf.
function buildKindTree(kinds) {
  const root = { key: "root", label: "root", children: [], folders: new Map() };
  for (const k of kinds || []) {
    const cat = (k.category || "general").split("/").map(s => s.trim()).filter(Boolean);
    let node = root;
    let path = "root";
    for (const seg of cat) {
      path += "/" + seg;
      if (!node.folders.has(seg)) {
        const child = { key: path, label: seg, children: [], folders: new Map(), kinds: [] };
        node.folders.set(seg, child);
        node.children.push(child);
      }
      node = node.folders.get(seg);
    }
    (node.kinds || (node.kinds = [])).push(k);
  }
  // Sort folders + kinds alphabetically for stable display.
  const sortRec = (n) => {
    n.children.sort((a, b) => a.label.localeCompare(b.label));
    (n.kinds || []).sort((a, b) => (a.display_name || a.kind).localeCompare(b.display_name || b.kind));
    n.children.forEach(sortRec);
  };
  sortRec(root);
  return root;
}

// Collect all folder keys (for expand-all on search).
function allFolderKeys(node, acc = []) {
  for (const c of node.children || []) { acc.push(c.key); allFolderKeys(c, acc); }
  return acc;
}

// One kind leaf — draggable onto the canvas + click-to-drop. Carries the kind
// name in the drag payload (ComfyUI uses application/x-comfy-node similarly).
function KindLeaf({ k, depth, onPick, onDragKind }) {
  const inT = k.domain_storage_type, outT = k.codomain_storage_type;
  const isConnector = !!k.__connector;
  // A SOURCE connector reads/writes one type → "source · <type>". A STRATEGY
  // lands records into its target (codomain) type → "stream into <type>".
  const title = isConnector ? `source · ${outT || inT}`
    : (!outT || outT === '*') ? `applies while streaming into any target`
    : `stream into ${outT}`;
  const dot = (t) => <span style={{ width: 7, height: 7, borderRadius: 9, background: typeColor(t), display: "inline-block" }} />;
  return (
    <button
      onClick={() => onPick?.(k)}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("application/x-cortex-kind", k.kind); onDragKind?.(k); }}
      title={`${title}${k.flow_endpoint ? "\n" + k.flow_endpoint : ""}`}
      className="w-full text-left py-1 rounded hover:bg-white/10 flex items-center gap-1.5"
      style={{ paddingLeft: 8 + depth * 14, paddingRight: 6, color: "var(--flow-text)" }}
    >
      <Cpu size={11} className="shrink-0 opacity-80" />
      <span className="flex-1 truncate">{k.display_name || k.kind}</span>
      <span className="shrink-0 flex items-center gap-0.5">
        {inT && dot(inT)}
        {inT && outT && <span className="text-page-muted">→</span>}
        {outT && dot(outT)}
      </span>
    </button>
  );
}

// Recursive folder row + its children. Collapsible.
function TreeFolder({ node, depth, expanded, toggle, onPick, onDragKind }) {
  const open = expanded.has(node.key);
  const count = useMemo(() => {
    let n = 0; const walk = (x) => { n += (x.kinds || []).length; (x.children || []).forEach(walk); }; walk(node); return n;
  }, [node]);
  return (
    <div>
      <button
        onClick={() => toggle(node.key)}
        className="w-full text-left py-1 rounded hover:bg-white/5 flex items-center gap-1"
        style={{ paddingLeft: 6 + depth * 14, color: "var(--flow-text)" }}
      >
        {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
        <span className="flex-1 truncate uppercase tracking-wide text-[10px]">{node.label}</span>
        <span className="text-[9px] opacity-70 pr-1">{count}</span>
      </button>
      {open && (
        <div>
          {node.children.map(c => (
            <TreeFolder key={c.key} node={c} depth={depth + 1} expanded={expanded} toggle={toggle} onPick={onPick} onDragKind={onDragKind} />
          ))}
          {(node.kinds || []).map(k => (
            <KindLeaf key={k.kind} k={k} depth={depth + 1} onPick={onPick} onDragKind={onDragKind} />
          ))}
        </div>
      )}
    </div>
  );
}

const LIBW_KEY = "cortex.pipeline.libwidth";
const LIBW_MIN = 180, LIBW_MAX = 480, LIBW_DEFAULT = 224;
function NodeLibrary({ kinds, connectors, existingSources, onPick, onPickConnector, onPlaceSource, onDragKind, workflows, activeWorkflow, onSelectWorkflow, onAddWorkflow, onRenameWorkflow, onDeleteWorkflow }) {
  const [q, setQ] = useState("");

  // Unified catalog item list, in the product's TWO groups:
  //   • Sources  — every connector node-type (file / DBs / warehouses / object
  //                stores). A source can both produce and consume, so we collapse
  //                the old load_*/save_* pair for the same storage type into one
  //                Source entry. Carries __connector so the leaf opens the config
  //                form. Both ports shown (storage type on each side).
  //   • Strategies — the effects applied while streaming records INTO a target
  //                (processing-rules / dedup / enrichment / stream-into). These
  //                are the `kinds` (category 'strategy/*'); dropped as nodes.
  const items = useMemo(() => {
    const byType = new Map();
    for (const c of connectors || []) {
      const stype = c.storage_type_out || c.storage_type_in;
      if (!stype) continue;
      // Prefer the load_* variant as the canonical config entry for a type
      // (file only has a load variant; pg/mongo have both → keep one).
      if (byType.has(stype) && c.role !== 'load') continue;
      const sub = (c.category || 'general').split('/')[1] || 'general'; // file|relational|document|warehouse|object
      byType.set(stype, {
        kind: `source_${stype}`, display_name: storageType(stype).label,
        category: `Sources/${sub}`,
        domain_storage_type: stype, codomain_storage_type: stype,
        __connector: c,
      });
    }
    const sources = [...byType.values()];
    // NOTE: strategies are deliberately NOT listed as library items. A strategy
    // node may ONLY come into existence by CONNECTING two sources (the
    // connect-time picker decides the kind). The library lists sources only —
    // connector types (create a new source) + existing sources (reuse one).
    // EXISTING sources already registered in the project (from /api/sources).
    // Dropping one places a NODE that reuses the existing source — no new source.
    // Grouped under "Existing sources/<type>"; carries __existing for the router.
    const existing = (existingSources || []).map(s => ({
      kind: `existing_${s.origin}_${s.entity_id ?? s.id}`,
      display_name: s.source_name,
      category: `Existing sources/${storageType(s.kind || s.source_type).label}`,
      domain_storage_type: s.kind || s.source_type,
      codomain_storage_type: s.kind || s.source_type,
      __existing: { origin: s.origin, entity_id: s.entity_id ?? s.id, name: s.source_name, type: s.kind || s.source_type },
    }));
    return [...sources, ...existing];
  }, [connectors, existingSources]);

  // Resizable width — drag the right edge. Persisted in localStorage, clamped.
  const [width, setWidth] = useState(() => {
    const v = parseInt(typeof localStorage !== "undefined" ? localStorage.getItem(LIBW_KEY) : "", 10);
    return Number.isFinite(v) ? Math.min(LIBW_MAX, Math.max(LIBW_MIN, v)) : LIBW_DEFAULT;
  });
  const dragRef = useRef(null);
  const onResizeDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX, startW = dragRef.current?.offsetWidth || width;
    const onMove = (ev) => {
      const w = Math.min(LIBW_MAX, Math.max(LIBW_MIN, startW + (ev.clientX - startX)));
      setWidth(w);
    };
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const w = Math.min(LIBW_MAX, Math.max(LIBW_MIN, startW + (ev.clientX - startX)));
      try { localStorage.setItem(LIBW_KEY, String(Math.round(w))); } catch {}
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [width]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const needle = q.toLowerCase();
    return items.filter(k =>
      `${k.display_name} ${k.kind} ${k.category} ${k.domain_storage_type} ${k.codomain_storage_type}`.toLowerCase().includes(needle)
    );
  }, [items, q]);
  const tree = useMemo(() => buildKindTree(filtered), [filtered]);

  // Leaf click router: an EXISTING source places a node (reuses the source);
  // a connector TYPE opens the config form (creates a new source); a strategy
  // kind drops a strategy node.
  const pick = useCallback((k) => {
    if (k.__existing) onPlaceSource?.(k.__existing);
    else if (k.__connector) onPickConnector?.(k.__connector);
    else onPick?.(k);
  }, [onPick, onPickConnector, onPlaceSource]);

  // Expanded folder keys. Default: ALL COLLAPSED (the user opens the group they
  // want). When a search query is active, auto-expand all matching folders so
  // results are visible (like ComfyUI's expand-on-search); clearing the query
  // collapses everything again.
  const [expanded, setExpanded] = useState(() => new Set());
  const allKeys = useMemo(() => allFolderKeys(tree), [tree]);
  useEffect(() => { setExpanded(q ? new Set(allKeys) : new Set()); }, [q, allKeys.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = useCallback((key) => setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }), []);

  return (
    <div ref={dragRef} className="pipeline-node-library shrink-0 h-full flex flex-col border-r border-border bg-card-bg/95 text-xs relative" style={{ width }} data-testid="pipeline-node-library">
      <div className="px-2 pt-2 pb-1 text-[11px] font-semibold flex items-center gap-1.5" style={{ color: "var(--flow-text)" }}>
        <Boxes size={13} /> Node library
      </div>
      <div className="flex items-center gap-1 mx-2 mb-2 px-1.5 py-1 rounded border border-border" style={{ color: "var(--flow-text-muted)" }}>
        <Search size={12} />
        <input
          value={q} onChange={e => setQ(e.target.value)} placeholder="Search nodes…"
          className="bg-transparent outline-none flex-1"
          style={{ color: "var(--flow-text)" }}
          data-testid="node-library-search"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-1 pb-2">
        {tree.children.length === 0 && (tree.kinds || []).length === 0 && <div className="px-2 py-2" style={{ color: "var(--flow-text-muted)" }}>No nodes.</div>}
        {tree.children.map(c => (
          <TreeFolder key={c.key} node={c} depth={0} expanded={expanded} toggle={toggle} onPick={pick} onDragKind={onDragKind} />
        ))}
        {/* kinds with no category land at root */}
        {(tree.kinds || []).map(k => (
          <KindLeaf key={k.kind} k={k} depth={0} onPick={pick} onDragKind={onDragKind} />
        ))}
      </div>

      {/* ── Workflows section (under Node Library) — open/rename/delete/add ── */}
      {workflows && (
        <WorkflowsPanel
          workflows={workflows} activeWorkflow={activeWorkflow}
          onSelect={onSelectWorkflow} onAdd={onAddWorkflow}
          onRename={onRenameWorkflow} onDelete={onDeleteWorkflow}
        />
      )}

      {/* Resize handle — drag the right edge to widen/narrow the library. */}
      <div
        onMouseDown={onResizeDown}
        data-testid="node-library-resize"
        title="Drag to resize"
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-white/20"
        style={{ transform: "translateX(50%)" }}
      />
    </div>
  );
}

// ── Bottom dock: docked, resizable, TABBED panel (mirrors the Node Library) ──
// Sits at the bottom of the canvas area as a flex child (NOT an overlay) so it
// never covers the graph or the zoom controls. Collapsed = just the tab bar;
// expanded = tab bar + body at a draggable height. `tabs` = [{id,label,content}].
const DOCKH_KEY = "cortex.pipeline.dockheight";
const DOCKH_MIN = 120, DOCKH_DEFAULT = 240;
function BottomDock({ tabs }) {
  const list = (tabs || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(list[0]?.id);
  const [height, setHeight] = useState(() => {
    const v = parseInt(typeof localStorage !== "undefined" ? localStorage.getItem(DOCKH_KEY) : "", 10);
    return Number.isFinite(v) ? v : DOCKH_DEFAULT;
  });
  const maxH = () => Math.round((typeof window !== "undefined" ? window.innerHeight : 800) * 0.6);
  const onResizeDown = useCallback((e) => {
    e.preventDefault();
    if (!open) return;
    const startY = e.clientY, startH = height;
    const onMove = (ev) => setHeight(Math.min(maxH(), Math.max(DOCKH_MIN, startH - (ev.clientY - startY))));
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const h = Math.min(maxH(), Math.max(DOCKH_MIN, startH - (ev.clientY - startY)));
      try { localStorage.setItem(DOCKH_KEY, String(Math.round(h))); } catch {}
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [open, height]);

  if (!list.length) return null;
  const activeTab = list.find(t => t.id === active) || list[0];

  return (
    <div className="pipeline-bottom-dock shrink-0 w-full border-t border-border bg-card-bg/95 text-xs relative flex flex-col" data-testid="pipeline-bottom-dock">
      {/* Resize handle on the TOP edge (only meaningful when open). */}
      {open && (
        <div
          onMouseDown={onResizeDown}
          data-testid="bottom-dock-resize"
          title="Drag to resize"
          className="absolute top-0 left-0 w-full h-1.5 cursor-row-resize hover:bg-white/20"
          style={{ transform: "translateY(-50%)" }}
        />
      )}
      {/* Tab bar (always visible). Clicking a tab opens the dock; clicking the
          active tab again toggles it closed. */}
      <div className="flex items-center gap-1 px-2 py-1.5" style={{ color: "var(--flow-text)" }}>
        {list.map(t => (
          <button
            key={t.id}
            data-testid={`dock-tab-${t.id}`}
            onClick={() => { if (open && active === t.id) { setOpen(false); } else { setActive(t.id); setOpen(true); } }}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[12px] ${open && active === t.id ? "bg-white/10" : "hover:bg-white/5"}`}
            style={{ color: "var(--flow-text)" }}
          >
            {open && active === t.id ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
            {t.label}{t.count != null && <span style={{ color: "var(--flow-text-muted)" }}> ({t.count})</span>}
          </button>
        ))}
      </div>
      {open && (
        <div className="overflow-auto border-t border-border" style={{ height, color: "var(--flow-text)" }}>
          {activeTab.content}
        </div>
      )}
    </div>
  );
}

// ── Workflow tab strip: IDE-style editor tabs across the top of the canvas ───
// Each open workflow is a tab; active tab connects to the canvas below. Double-
// click (or the rename on a new tab) edits the name inline; × closes/deletes; ＋
// adds a workflow. Mirrors a VS Code editor tab row.
function WorkflowTabStrip({ workflows, activeWorkflow, onSelect, onAdd, onRename, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const startEdit = (wf) => { setEditingId(wf.id); setDraft(wf.name); };
  const commit = () => {
    const id = editingId, name = draft.trim();
    setEditingId(null);
    if (id != null && name) onRename?.(id, name);
  };
  const list = workflows || [];
  return (
    <div className="pipeline-workflow-tabs flex items-stretch h-9 shrink-0 overflow-x-auto border-b border-border"
      style={{ background: "var(--flow-node-bg, #11182a)" }} data-testid="workflow-tabstrip">
      {list.map(wf => {
        const active = wf.id === activeWorkflow;
        return (
          <div key={wf.id}
            data-testid={`wf-tab-${wf.id}`}
            onClick={() => onSelect?.(wf.id)}
            onDoubleClick={() => startEdit(wf)}
            title={wf.name}
            className={`group relative flex items-center gap-2 px-3 max-w-[200px] cursor-pointer border-r border-border text-[13px] select-none`}
            style={{
              color: active ? "var(--flow-text)" : "var(--flow-text-muted)",
              background: active ? "var(--flow-canvas-bg)" : "transparent",
              borderTop: active ? "2px solid var(--nav-active, #4f8ef7)" : "2px solid transparent",
            }}
          >
            {editingId === wf.id ? (
              <input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if (e.key === "Enter") commit(); else if (e.key === "Escape") setEditingId(null); }}
                onClick={e => e.stopPropagation()}
                data-testid="wf-tab-input"
                className="bg-transparent outline-none border-b border-white/40 w-[140px]"
                style={{ color: "var(--flow-text)" }}
              />
            ) : (
              <span className="truncate">{wf.name}</span>
            )}
            {list.length > 1 && editingId !== wf.id && (
              <span role="button" title="Close workflow"
                onClick={(e) => { e.stopPropagation(); onDelete?.(wf); }}
                className="opacity-0 group-hover:opacity-70 hover:opacity-100 shrink-0"
                style={{ color: "var(--flow-text-muted)" }}>
                <X size={13} />
              </span>
            )}
          </div>
        );
      })}
      <button onClick={onAdd} data-testid="wf-tab-add" title="New workflow"
        className="flex items-center justify-center px-3 hover:bg-white/5"
        style={{ color: "var(--flow-text-muted)" }}>
        <Plus size={15} />
      </button>
    </div>
  );
}

// ── Workflows side-panel section (under Node Library) — list of workflows ────
// Click a row to open it (active highlighted); double-click to rename inline; ×
// to delete; ＋ in the header to add.
function WorkflowsPanel({ workflows, activeWorkflow, onSelect, onAdd, onRename, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const startEdit = (wf) => { setEditingId(wf.id); setDraft(wf.name); };
  const commit = () => { const id = editingId, name = draft.trim(); setEditingId(null); if (id != null && name) onRename?.(id, name); };
  return (
    <div className="shrink-0 border-t border-border" style={{ maxHeight: "40%", display: "flex", flexDirection: "column" }} data-testid="workflows-panel">
      <div className="px-2 pt-2 pb-1 text-[11px] font-semibold flex items-center justify-between" style={{ color: "var(--flow-text)" }}>
        <span className="flex items-center gap-1.5"><WorkflowIcon size={13} /> Workflows</span>
        <button onClick={onAdd} title="New workflow" data-testid="workflows-add"
          className="hover:bg-white/10 rounded p-0.5" style={{ color: "var(--flow-text-muted)" }}><Plus size={13} /></button>
      </div>
      <div className="overflow-auto px-1 pb-2">
        {(workflows || []).map(wf => {
          const active = wf.id === activeWorkflow;
          return (
            <div key={wf.id}
              data-testid={`wf-row-${wf.id}`}
              onClick={() => onSelect?.(wf.id)}
              onDoubleClick={() => startEdit(wf)}
              className={`group flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer ${active ? "bg-white/10" : "hover:bg-white/5"}`}
              style={{ color: "var(--flow-text)" }}
            >
              <WorkflowIcon size={11} className="shrink-0 opacity-70" />
              {editingId === wf.id ? (
                <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit}
                  onKeyDown={e => { if (e.key === "Enter") commit(); else if (e.key === "Escape") setEditingId(null); }}
                  onClick={e => e.stopPropagation()} data-testid="wf-row-input"
                  className="bg-transparent outline-none border-b border-white/40 flex-1" style={{ color: "var(--flow-text)" }} />
              ) : (
                <span className="flex-1 truncate">{wf.name}</span>
              )}
              {(workflows || []).length > 1 && editingId !== wf.id && (
                <span role="button" title="Delete workflow" onClick={(e) => { e.stopPropagation(); onDelete?.(wf); }}
                  className="opacity-0 group-hover:opacity-70 hover:opacity-100 shrink-0" style={{ color: "var(--flow-text-muted)" }}>
                  <X size={12} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Resolve the storage TYPE on each side of a candidate connection. EVERY source
// (load- or save-backed node) can both produce and consume records, so a source
// exposes its storage type on BOTH sides. A strategy node exposes its
// domain/codomain types. Whether a wire is *valid* is decided by strategy
// availability for the target type — not by raw type equality (the old rule).
function outTypeOf(node) {
  if (!node) return null;
  if (node.type === "load" || node.type === "save") return node.data?.storageType;
  if (node.type === "strategy") return node.data?.codomainType;
  return null;
}
function inTypeOf(node) {
  if (!node) return null;
  if (node.type === "load" || node.type === "save") return node.data?.storageType;
  if (node.type === "strategy") return node.data?.domainType;
  return null;
}
// Resolve the STORAGE ID flowing OUT of a node's output socket. A source node
// exposes its own storage; a strategy node its codomain. (Pending strategy nodes
// have no row yet → null.)
function outStorageId(node) {
  if (!node) return null;
  if (node.type === "load" || node.type === "save") return node.data?.storage?.storage_id;
  if (node.type === "strategy") return node.data?.strategy?.codomain_storage_id || null;
  return null;
}
// Resolve the STORAGE ID consumed at a node's input socket. A source node
// exposes its own storage; a strategy node its domain.
function inStorageId(node) {
  if (!node) return null;
  if (node.type === "load" || node.type === "save") return node.data?.storage?.storage_id;
  if (node.type === "strategy") return node.data?.strategy?.domain_storage_id || null;
  return null;
}

// localStorage-backed node-position store, keyed by project + node id, so a
// dragged layout survives reload (per-browser). Cheap, no migration; can be
// upgraded to a backend layout endpoint later for cross-device.
const POS_KEY = (project) => `cortex.pipeline.pos.${project || "default"}`;
function loadPositions(project) {
  try { return JSON.parse(localStorage.getItem(POS_KEY(project)) || "{}"); } catch { return {}; }
}
function savePosition(project, nodeId, pos) {
  try {
    const all = loadPositions(project);
    all[nodeId] = { x: Math.round(pos.x), y: Math.round(pos.y) };
    localStorage.setItem(POS_KEY(project), JSON.stringify(all));
  } catch { /* storage full / disabled — positions just won't persist */ }
}

function Inner({ project, nodeGraph, storages, strategies, kinds, catalog, existingSources, onClickStrategy, onClickStorage, onCreateStrategy, onDeleteStrategy, onDeleteNode, onPickConnector, onPlaceSource,
  workflows, activeWorkflow, onSelectWorkflow, onAddWorkflow, onRenameWorkflow, onDeleteWorkflow,
  // showWorkflowsPanel=false hides the node-library's bottom Workflows section
  // (the Pipeline page's light rail owns that list now); the tab strip stays.
  showWorkflowsPanel = true,
  title, toolbar, banner, dockTabs }) {
  const kindsByName = useMemo(() => new Map((kinds || []).map(k => [k.kind, k])), [kinds]);
  // STRATEGY kinds for the library + connect-time picker: prefer the catalog's
  // already-filtered list (engine-only kinds like file_to_parquet excluded);
  // fall back to the raw kinds registry. Each carries storage_type_out = the
  // TARGET type it streams into (what the picker keys off).
  const strategyKinds = useMemo(
    () => catalog?.strategies || catalog?.transforms || (kinds || []),
    [catalog, kinds]
  );
  // Use the node/link graph whenever a nodeGraph is provided (workflows exist) —
  // even when EMPTY (an empty workflow must show an empty canvas, NOT fall back
  // to the legacy storage projection). The legacy fallback is only for callers
  // that pass no nodeGraph at all (e.g. ?mock=).
  const useNodes = !!nodeGraph;
  const savedPositions = useMemo(() => loadPositions(project), [project, storages, strategies, nodeGraph]);
  // Which parallel-transform groups the user expanded on THIS canvas (visual
  // only — collapsing/expanding never touches the data layer).
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = useCallback((key) => {
    if (!key) return;
    setExpandedGroups((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);
  const built = useMemo(
    () => {
      const g = useNodes
        ? buildGraphFromNodes(nodeGraph, savedPositions, expandedGroups)
        : buildPipelineGraph(storages, strategies, kindsByName, savedPositions);
      for (const n of g.nodes) if (n.data?.groupKey) n.data.onToggleGroup = toggleGroup;
      return g;
    },
    [useNodes, nodeGraph, storages, strategies, kindsByName, savedPositions, expandedGroups, toggleGroup]
  );

  // Pending (not-yet-saved) strategy nodes dropped from the palette. They live
  // only in canvas state until both their sockets are wired, at which point we
  // create_strategy and the server-backed projection replaces them.
  const [pending, setPending] = useState([]); // [{id, kind, kindDef, domainType, codomainType, domainStorageId, codomainStorageId, position}]

  const [nodes, setNodes, onNodesChange] = useNodesState(built.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(built.edges);

  // Re-seed only on real topology change; preserve dragged positions (quiet
  // polls must not snap nodes back). Pending nodes are merged in on top.
  const sig = `${built.nodes.map(n => n.id).join("|")}~~${built.edges.map(e => e.id).join("|")}~~${pending.map(p => `${p.id}:${p.domainStorageId || ""}:${p.codomainStorageId || ""}`).join("|")}`;
  useEffect(() => {
    const pendingNodes = pending.map(p => ({
      id: p.id, type: "strategy", position: p.position,
      data: { kind: p.kind, displayName: (p.kindDef?.display_name || p.kind) + " (new)", domainType: p.domainType, codomainType: p.codomainType, paramCount: 0, pending: true },
      style: { opacity: 0.85 },
    }));
    setNodes(prev => {
      const p = new Map(prev.map(n => [n.id, n.position]));
      const base = built.nodes.map(n => (p.has(n.id) ? { ...n, position: p.get(n.id) } : n));
      return [...base, ...pendingNodes];
    });
    setEdges(built.edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, setNodes, setEdges]);

  const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const pendingById = useMemo(() => new Map(pending.map(p => [p.id, p])), [pending]);

  // Open a node's detail (storage page / strategy inspector) on DOUBLE-click.
  // Single-click must stay a plain reactflow SELECT so a node can be selected for
  // delete (Backspace) or wiring — a single-click that navigated to /storages/…
  // made source nodes impossible to select/delete on the canvas (bug 1).
  const onNodeDoubleClick = useCallback((_e, node) => {
    if (node?.data?.strategy) onClickStrategy?.(node.data.strategy);
    else if (node?.data?.storage) onClickStorage?.(node.data.storage);
  }, [onClickStrategy, onClickStorage]);

  // The ONLY valid topology is Source → Strategy → Source. A strategy is always
  // TYPED-SOCKET rule (ComfyUI-style: a link is valid iff output.type matches
  // input.type, with '*' as wildcard). A STRATEGY is a node whose sockets connect
  // ONLY to its own source types:
  //   • strategy → strategy : NEVER (a strategy is not a source),
  //   • source → strategy   : valid iff the strategy's DOMAIN type === source type
  //                            (strategy can only consume its own source type),
  //   • strategy → source   : valid iff the strategy's CODOMAIN type === source type
  //                            (strategy can only land into its own target type),
  //   • source → source     : allowed — the picker inserts the matching strategy.
  // '*' on a strategy socket means target-agnostic (matches any source type).
  const typeMatch = (a, b) => a === "*" || b === "*" || a === b;
  const isValidConnection = useCallback((c) => {
    if (!c || c.source === c.target) return false;
    const src = nodeById.get(c.source), tgt = nodeById.get(c.target);
    if (!src || !tgt) return false;
    const srcIsStrategy = src.type === "strategy";
    const tgtIsStrategy = tgt.type === "strategy";
    if (srcIsStrategy && tgtIsStrategy) return false;       // strategy ↔ strategy forbidden
    const ot = outTypeOf(src), it = inTypeOf(tgt);
    if (!ot || !it) return false;
    if (srcIsStrategy || tgtIsStrategy) return typeMatch(ot, it); // strategy ↔ source: its own type only
    return true;                                            // source → source: picker chooses the strategy
  }, [nodeById]);

  // Confirmed connection. Resolve the concrete storage ids on each side and,
  // when we have a pending node with BOTH sockets wired, persist it via
  // create_strategy. Connecting one pending socket just records the storage id.
  const onConnect = useCallback((c) => {
    const src = nodeById.get(c.source), tgt = nodeById.get(c.target);
    if (!src || !tgt) return;

    // Place a new strategy node on the SHORTEST PATH between its two sources —
    // the midpoint of their positions (nudged left by half a card so the node is
    // centred between them, not hanging off the right edge).
    const midpoint = (a, b) => {
      const pa = a?.position, pb = b?.position;
      if (!pa || !pb || !Number.isFinite(pa.x) || !Number.isFinite(pb.x)) return undefined;
      return { x: Math.round((pa.x + pb.x) / 2 - CARD_W / 2), y: Math.round((pa.y + pb.y) / 2) };
    };

    // A STRATEGY is always part of a SOURCE's input — it never connects to
    // another strategy. Reject strategy→strategy outright (pending nodes are
    // strategies too). The only valid shape is Source → Strategy → Source.
    if (src.type === "strategy" && tgt.type === "strategy") return;

    // Case 1: target is a pending node → its DOMAIN = source's output storage.
    // Case 2: source is a pending node → its CODOMAIN = target's input storage.
    // Compute the merged pending record, then apply state + fire create as
    // SIDE-EFFECTS (never call setState/onCreate from inside a setState updater).
    const tgtPending = pendingById.get(tgt.id);
    const srcPending = pendingById.get(src.id);

    const applyPending = (id, patch) => {
      const cur = pendingById.get(id);
      if (!cur) return;
      const merged = { ...cur, ...patch };
      if (merged.domainStorageId && merged.codomainStorageId) {
        // Both sockets wired → persist and drop the pending node (the server
        // projection re-renders the saved strategy on reload). Keep the pending
        // node's dropped position so the saved strategy lands where the user put it.
        onCreateStrategy?.({ kind: merged.kind, domain_storage_id: merged.domainStorageId, codomain_storage_id: merged.codomainStorageId, position: merged.position });
        setPending(prev => prev.filter(x => x.id !== id));
      } else {
        setPending(prev => prev.map(x => x.id === id ? merged : x));
      }
    };

    if (tgtPending) {
      const dom = outStorageId(src);
      if (dom) applyPending(tgt.id, { domainStorageId: dom });
      return;
    }
    if (srcPending) {
      const cod = inStorageId(tgt);
      if (cod) applyPending(src.id, { codomainStorageId: cod });
      return;
    }

    // Case 3: connecting two EXISTING sources (no pending node). This is "stream
    // src INTO tgt". A connection is never bare — a STRATEGY (the effect applied
    // while landing records into tgt) sits between them. Strategy availability is
    // keyed by the TARGET source TYPE only. Offer the strategies for that type:
    //   • 0  → tell the user none are implemented (don't connect),
    //   • 1  → drop it straight in,
    //   • >1 → open a picker popover.
    const dom = outStorageId(src), cod = inStorageId(tgt);
    const it = inTypeOf(tgt);               // the target (consuming) source type
    const ot = outTypeOf(src);              // the upstream (producing) source type
    if (!dom || !cod || !it) return;
    // Offer only strategies whose typed sockets match BOTH ends ('*' = any):
    // domain must accept the upstream source type, codomain the target type.
    // (Without the domain check, internal-domain kinds like enrichment showed up
    // for a file→postgres wire and the backend then rejected them.)
    const tmatch = (a, b) => a === '*' || b === '*' || a === b;
    // The catalog shapes kinds as storage_type_in (domain) / storage_type_out
    // (codomain) — NOT domain_storage_type/codomain_storage_type. Match on the
    // fields the catalog actually carries, with a fallback to the raw-registry
    // names so both list shapes work. (Reading domain_storage_type here was the
    // bug: it's undefined on catalog items → tmatch(undefined,'file')=false →
    // ALL kinds filtered out → "No strategies …" for every wire.)
    const outOf = (k) => k.storage_type_out ?? k.codomain_storage_type;
    const inOf  = (k) => k.storage_type_in  ?? k.domain_storage_type;
    const available = (strategyKinds || []).filter(k =>
      tmatch(outOf(k), it) && tmatch(inOf(k), ot));
    if (available.length === 0) {
      setStrategyPicker({ none: true, targetType: it, x: 0, y: 0 });
      return;
    }
    const mid = midpoint(src, tgt);
    if (available.length === 1) {
      onCreateStrategy?.({ kind: available[0].kind, domain_storage_id: dom, codomain_storage_id: cod, position: mid });
      return;
    }
    // Multiple strategies → ask which effect to apply while streaming in.
    setStrategyPicker({ targetType: it, domain_storage_id: dom, codomain_storage_id: cod, options: available, position: mid });
  }, [nodeById, pendingById, strategyKinds, onCreateStrategy]);

  // Connect-time strategy picker state ({options,…} | {none:true,targetType} | null).
  const [strategyPicker, setStrategyPicker] = useState(null);

  // Esc closes the picker. It already closed on a backdrop click and the X, but
  // not on Escape — every other modal in the app does, so reaching for it and
  // having nothing happen reads as a stuck dialog. Capture phase, because
  // ReactFlow also listens for keys on the canvas.
  useEffect(() => {
    if (!strategyPicker) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setStrategyPicker(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [strategyPicker]);

  const chooseStrategy = useCallback((k) => {
    const p = strategyPicker;
    if (!p || !k) { setStrategyPicker(null); return; }
    onCreateStrategy?.({ kind: k.kind, domain_storage_id: p.domain_storage_id, codomain_storage_id: p.codomain_storage_id, position: p.position });
    setStrategyPicker(null);
  }, [strategyPicker, onCreateStrategy]);

  // Delete selected strategy nodes (Backspace/Delete). Pending nodes just drop
  // from canvas state; saved strategy rows go to the backend. Load/Save nodes
  // are storages — recipe ops handled elsewhere, ignored here.
  const onNodesDelete = useCallback((deleted) => {
    for (const n of deleted || []) {
      // A collapsed GROUP card stands for N nodes — deleting it would silently
      // delete only its first member. Expand first, delete individually.
      if ((n.data?.groupCount || 0) > 1) continue;
      if (pendingById.has(n.id)) { setPending(prev => prev.filter(x => x.id !== n.id)); continue; }
      // Node-native graph: any node (load/save/transform) deletes via the node API.
      if (n.data?.node?.id != null) { onDeleteNode?.(n.data.node); continue; }
      // Legacy projection: strategy edges delete via the strategy API.
      if (n.type === "strategy" && n.data?.strategy?.id != null) onDeleteStrategy?.(n.data.strategy);
    }
  }, [onDeleteStrategy, onDeleteNode, pendingById]);

  // Persist a dragged node's position (per project, per node).
  const onNodeDragStop = useCallback((_e, node) => {
    if (node?.position) {
      savePosition(project, node.id, node.position);
      // Keep a pending node's stored position in sync too.
      setPending(prev => prev.map(p => p.id === node.id ? { ...p, position: node.position } : p));
    }
  }, [project]);

  // Test-only hook: drive the EXACT connection path (onConnect) by node id,
  // so end-to-end wiring can be verified without simulating reactflow's
  // handle-drag plumbing (which is unreliable to drive headlessly). Mirrors a
  // real drag: source-output → target-input. Inert for real users — only
  // installed when an E2E test has opted in via `window.__CORTEX_E2E = true`
  // (the deployed bundle is a prod build, so a build-mode gate would strip it).
  useEffect(() => {
    if (typeof window === "undefined" || !window.__CORTEX_E2E) return;
    window.__pipelineConnect = (sourceId, targetId) => onConnect({ source: sourceId, target: targetId, sourceHandle: "out", targetHandle: "in" });
    return () => { try { delete window.__pipelineConnect; } catch {} };
  }, [onConnect]);

  // Drop a pending strategy node from the library. `position` is in flow
  // coordinates; default near the top-left of the canvas if not dragged to a
  // specific spot.
  const dropSeq = useRef(0);
  const rf = useReactFlow();
  const dropKind = useCallback((k, position) => {
    if (!k) return;
    // A library item is one of three things; only a real strategy kind drops a
    // pending node. An EXISTING source places a node that reuses the source; a
    // CONNECTOR type opens the config form. (Strategy kinds are no longer in the
    // library, but keep this dispatch so drag/drop can never make a stray node.)
    if (k.__existing) { onPlaceSource?.({ ...k.__existing, position }); return; }
    if (k.__connector) { onPickConnector?.(k.__connector, position); return; }
    dropSeq.current += 1;
    const id = `pending:${dropSeq.current}`;
    setPending(prev => [...prev, {
      id, kind: k.kind, kindDef: k,
      domainType: k.domain_storage_type, codomainType: k.codomain_storage_type,
      domainStorageId: null, codomainStorageId: null,
      position: position || { x: 120 + (prev.length * 30), y: 40 + (prev.length * 30) },
    }]);
  }, [onPlaceSource, onPickConnector]);
  const handlePick = useCallback((k) => dropKind(k), [dropKind]);

  // Test-only: reproduce the library-drag of a strategy kind, returning the
  // pending node id so an E2E test can drive source→strategy→source headlessly.
  useEffect(() => {
    if (typeof window === "undefined" || !window.__CORTEX_E2E) return;
    window.__pipelineDropKind = (kindName) => {
      const k = (strategyKinds || []).find(x => x.kind === kindName) || (kinds || []).find(x => x.kind === kindName);
      if (!k) return null;
      dropKind(k);
      return `pending:${dropSeq.current}`;
    };
    return () => { try { delete window.__pipelineDropKind; } catch {} };
  }, [dropKind, strategyKinds, kinds]);

  // Native drag from the library → drop on the canvas at the cursor. The drag
  // payload carries the kind name; resolve it back to the kind def and drop a
  // pending node at the flow position under the pointer.
  const draggedKindRef = useRef(null);
  const onDragKind = useCallback((k) => { draggedKindRef.current = k; }, []);
  const onCanvasDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }, []);
  const onCanvasDrop = useCallback((e) => {
    e.preventDefault();
    const name = e.dataTransfer.getData("application/x-cortex-kind") || draggedKindRef.current?.kind;
    // Prefer the full dragged item (carries __existing/__connector markers);
    // fall back to resolving the strategy kind by name.
    const k = draggedKindRef.current || (kinds || []).find(x => x.kind === name);
    draggedKindRef.current = null;
    if (!k) return;
    let position;
    try { position = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY }); } catch { position = undefined; }
    dropKind(k, position);
  }, [kinds, rf, dropKind]);

  return (
    // Full-page canvas: fills its parent (the page gives it the whole content
    // area). The Node Library + all overlays (title, toolbar, banner, runs
    // drawer, stats) float INSIDE the canvas over the reactflow graph.
    <div className="pipeline-canvas flow-studio-root dark relative flex h-full w-full" data-testid="pipeline-canvas">
      {/* The embedded studio CSS shrinks reactflow handles to ~5px, which makes
          the connection points hard to grab. Enlarge them within this canvas so
          they're an easy drag target (and a clearer affordance). */}
      <style>{`
        .pipeline-canvas .react-flow__handle { width: 11px; height: 11px; border-radius: 11px; }
        .pipeline-canvas .react-flow__handle-left { left: -6px; }
        .pipeline-canvas .react-flow__handle-right { right: -6px; }
      `}</style>
      <NodeLibrary kinds={strategyKinds} connectors={catalog?.connectors} existingSources={existingSources} onPick={handlePick} onPickConnector={onPickConnector} onPlaceSource={onPlaceSource} onDragKind={onDragKind}
        workflows={showWorkflowsPanel ? workflows : null} activeWorkflow={activeWorkflow} onSelectWorkflow={onSelectWorkflow}
        onAddWorkflow={onAddWorkflow} onRenameWorkflow={onRenameWorkflow} onDeleteWorkflow={onDeleteWorkflow} />
      {/* Canvas column = IDE tab strip + the graph (fills remaining space) ABOVE
          the bottom dock. */}
      <div className="flex-1 min-w-0 h-full flex flex-col">
        <WorkflowTabStrip
          workflows={workflows} activeWorkflow={activeWorkflow}
          onSelect={onSelectWorkflow} onAdd={onAddWorkflow}
          onRename={onRenameWorkflow} onDelete={onDeleteWorkflow}
        />
        <div className="relative flex-1 min-h-0" onDrop={onCanvasDrop} onDragOver={onCanvasDragOver}>
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes} onNodeDoubleClick={onNodeDoubleClick}
            isValidConnection={isValidConnection}
            onConnect={onConnect}
            onNodesDelete={onNodesDelete}
            onNodeDragStop={onNodeDragStop}
            deleteKeyCode={["Backspace", "Delete"]}
            fitView style={{ backgroundColor: "var(--flow-canvas-bg)" }}
          >
            <Background color="var(--flow-dot-color)" gap={20} size={1} />
            <Controls showInteractive={false} position="bottom-left" />
          </ReactFlow>

          {/* Floating overlays over the GRAPH. Title card removed — the tab strip
              names the active workflow; project/branch live in the page chrome. */}
          {toolbar && <div className="absolute top-3 right-3 z-20">{toolbar}</div>}
          <div className="absolute z-20 right-3 text-[11px] bg-card-bg/80 rounded px-2 py-1 pointer-events-none"
            style={{ top: 52, color: "var(--flow-text-muted)" }}>
            {built.nodes.length} nodes · {built.edges.length} links
            {pending.length > 0 && <span className="text-amber-400"> · {pending.length} unsaved</span>}
          </div>
          {banner && <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 max-w-[60%]">{banner}</div>}

          {/* Connect-time STRATEGY picker. When two sources are wired, choose the
              effect applied while streaming records INTO the target source. The
              options are the strategies available for the TARGET source TYPE. */}
          {strategyPicker && (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40"
              onClick={() => setStrategyPicker(null)}>
              <div className="rounded-lg shadow-xl border min-w-[320px] max-w-[420px]"
                style={{ background: "var(--flow-node-bg)", borderColor: "var(--flow-border, #334155)", color: "var(--flow-text)" }}
                onClick={(e) => e.stopPropagation()}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--flow-border, #334155)" }}>
                  <span className="font-semibold text-sm">
                    Stream into <span style={{ color: typeColor(strategyPicker.targetType) }}>{storageType(strategyPicker.targetType).label}</span>
                  </span>
                  <button onClick={() => setStrategyPicker(null)} className="opacity-70 hover:opacity-100"><X size={15} /></button>
                </div>
                {strategyPicker.none ? (
                  <div className="px-4 py-4 text-sm" style={{ color: "var(--flow-text-muted)" }}>
                    No strategies are implemented for streaming into a{" "}
                    <span style={{ color: typeColor(strategyPicker.targetType) }}>{storageType(strategyPicker.targetType).label}</span> source yet.
                    <div className="mt-2 text-xs">
                      Add a strategy for this source type (register a strategy kind whose target type is
                      <code className="mx-1">{strategyPicker.targetType}</code>) and author its engine impl
                      <code className="mx-1">cortex.strategy.&lt;kind&gt;.apply</code>.
                    </div>
                  </div>
                ) : (
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs" style={{ color: "var(--flow-text-muted)" }}>
                      Choose the effect to apply while landing records:
                    </div>
                    {strategyPicker.options.map((k) => (
                      <button key={k.kind} onClick={() => chooseStrategy(k)}
                        className="w-full text-left px-4 py-2 hover:bg-white/10 flex items-center gap-2">
                        <Cpu size={13} className="opacity-80 shrink-0" />
                        <span className="flex-1">{k.label || k.display_name || k.kind}</span>
                        <span className="text-xs font-mono opacity-60">{k.kind}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Bottom dock — resizable, tabbed (Runs). Docked, not overlaid. */}
        <BottomDock tabs={dockTabs} />
      </div>
    </div>
  );
}

export default function PipelineCanvas(props) {
  return (
    <ReactFlowProvider>
      <Inner {...props} />
    </ReactFlowProvider>
  );
}
